import { useEffect, useMemo, useState } from 'react';
import { fetchProductsFromHandles } from '../../../../utilities';

import { ProductColorOptionValue } from './ProductColorOptionValue';
import { ProductOptionValuesLabel } from '../ProductOptionValuesLabel';

export function ProductColorOptionValues({
  inventory,
  inventoryFetched,
  product,
  name,
  selectedOptions,
  setSelectedOptions,
  swatches,
  disableGroupHistory,
  enabledSoldOutColorSwatch,
  isSoldOut,
  soldOutQty,
  enabledGroupTabs,
  enabledGroupTabsLink,
  productTypeForGroupTabs,
  enabledGroupOptions,
  productTypeForGroupOptions,
  needsSelection,
  setNeedsSelection,
  optionChanges,
  groupedBreadcrumbs
}) {
  const tabsEnabled = enabledGroupTabs;
  const isProductTypePresent = productTypeForGroupTabs?.some(item => item?.productType === product?.productType);
  const isFullWidthTabs = productTypeForGroupTabs?.some(item => item?.fullWidthTabs === true);

  const optionsEnabled = enabledGroupOptions;
  const isProductTypePresentOptions = productTypeForGroupOptions?.some(item => item?.productType === product?.productType);

  const [groupingProductsMapByColor, setGroupingProductsMapByColor] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [selectedlifecycleSubgroup, setSelectedlifecycleSubgroup] = useState(false);
  const [groupedItems, setGroupedItems] = useState({ default: [] });
  const [selectedGroup, setSelectedGroup] = useState(null);

  const colorOptions = useMemo(() => {
    return product.grouping?.isTransformed
      ? product.grouping.options.find((option) => option.name === 'Color')
      : product.options.find((option) => option.name === 'Color');
  }, [product]);

  const selectedColor = selectedOptions?.[name];

  const findSelectedGroup = (groups, selectedColor) => {
    if (!groups) {
      return null;
    }
    
    for (const group of groups) {
      if (group.values.includes(selectedColor)) {
        return group;
      }
    }

    return null;
  };

  useEffect(() => {
    if (colorOptions && selectedColor) {
      const group = findSelectedGroup(colorOptions.groups, selectedColor);
      setSelectedGroup(group);
    }
  }, [colorOptions, selectedColor]);

  const groupByLifecycleSubgroups = (group) => {
    const groupedItems = {
      default: [],
    };

    group?.values.forEach((value) => {
      const product = groupingProductsMapByColor?.[value];
      if (product) {
        const lifecycleSubgroup = product?.metafields?.find(
          (metafield) => metafield?.key === 'lifecycle_subgroups'
        )?.value;

        if (lifecycleSubgroup) {
          const parsedSubgroup = JSON.parse(lifecycleSubgroup)[0]; // Parse and extract the first element
          if (!groupedItems[parsedSubgroup]) {
            groupedItems[parsedSubgroup] = [];
          }
          groupedItems[parsedSubgroup].push(value);
        } else {
          groupedItems.default.push(value);
        }
      } else {
        groupedItems.default.push(value);
      }
    });

    return groupedItems;
  };

  useEffect(() => {
    if (groupingProductsMapByColor && selectedGroup) {
      const groupedItems = groupByLifecycleSubgroups(selectedGroup);
      setGroupedItems(groupedItems);
      const hasMultipleKeys = Object.keys(groupedItems)?.length > 1;
      setSelectedlifecycleSubgroup(hasMultipleKeys);
    }
  }, [selectedGroup, groupingProductsMapByColor]);

  const renderColorOptions = (group) => {
    const groupedItems = groupByLifecycleSubgroups(group);
    const selectedValue = selectedOptions?.[name];

    return (
      <div className="flex flex-col gap-2.5">
        {Object.keys(groupedItems).map((subgroup) => {
          if (!groupedItems[subgroup] || groupedItems[subgroup].length === 0) {
            return null;
          }
          const selectedClass = groupedItems[subgroup].includes(selectedValue) ? 'block' : 'hidden';

          return (
            <div key={subgroup}>
              {subgroup !== 'default' && (
                <div className="flex items-center gap-2">
                  <h3 className="text-label text-darkGray">{subgroup}</h3>
                  <p className={`text-sm text-mediumGray ${selectedClass}`}>
                    {selectedValue.split('|').length > 1 ? selectedValue.split('|')[1].trim() : selectedValue}
                  </p>
                </div>
              )}
              <ul className="flex flex-wrap gap-[5px]">
                {groupedItems[subgroup].map((value) => {
                  const isSelected = selectedColor === value;

                  return (
                    <li key={value}>
                      <ProductColorOptionValue
                        groupingProductsMapByColor={groupingProductsMapByColor}
                        inventory={inventory}
                        isLoading={!inventoryFetched}
                        isSelected={isSelected}
                        name={name}
                        product={product}
                        selectedOptions={selectedOptions}
                        setSelectedOptions={setSelectedOptions}
                        value={value}
                        swatches={swatches}
                        disableGroupHistory={disableGroupHistory}
                        enabledSoldOutColorSwatch={enabledSoldOutColorSwatch}
                        isSoldOut={isSoldOut}
                        soldOutQty={soldOutQty}
                        needsSelection={needsSelection}
                        setNeedsSelection={setNeedsSelection}
                        optionChanges={optionChanges}
                        groupedBreadcrumbs={groupedBreadcrumbs}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    );
  };

  const validGroups = colorOptions.groups?.filter(group => group?.values.length > 0);

  const renderTabs = (group, index) => {
    const groupHasSelectedColor = group?.values.includes(selectedColor);
    const groupName = group.name === 'Default' ? 'Solids' : group.name;
    const colorName = groupName.toLowerCase().trim().endsWith('colors')
      ? groupName
      : `${groupName}`;

    return (
      <ProductOptionValuesLabel
        key={index}
        name={colorName}
        groupingProductsMapByColor={groupingProductsMapByColor}
        groupValues={group?.values}
        selectedOptions={selectedOptions}
        selectedValue={groupHasSelectedColor ? selectedColor : null}
        selectedColor={selectedColor}
        tabsEnabled={tabsEnabled}
        tabsLinkEnabled={enabledGroupTabsLink}
        disableGroupHistory={disableGroupHistory}
        needsSelection={needsSelection}
        optionChanges={optionChanges}
        isProductTypePresent={isProductTypePresent}
        isFullWidthTabs={isFullWidthTabs}
        index={index}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        isColor={true}
        groupedBreadcrumbs={groupedBreadcrumbs}
      />
    );
  };

  useEffect(() => {
    if (!product?.grouping?.isTransformed) return;

    const fetchProductsForMap = async () => {
      let productHandles = product.grouping.products || [];
      if (product.grouping.subgroups?.length) {
        product.grouping.subgroups.forEach((subgroup) => {
          productHandles = [...productHandles, ...subgroup.products];
        });
      }
      const products = await fetchProductsFromHandles(productHandles);
      setGroupingProductsMapByColor(
        products?.reduce((acc, groupProduct) => {
          const color = groupProduct.optionsMap?.Color?.[0];
          if (!color) return acc;
          return {
            ...acc,
            [color]: groupProduct,
          };
        }, {})
      );
    };
    fetchProductsForMap();
  }, [product?.id]);

  return tabsEnabled && isProductTypePresent ? (
    <div>
      {colorOptions.hasSubGroups ? (
        <div className="flex flex-col gap-2.5">
          <div className="w-full">
            <nav className="flex flex-wrap gap-[5px]" aria-label="Color Tabs" role="tablist">
              {validGroups.map(renderTabs)}
            </nav>
          </div>
          {validGroups.map((group, index) => {
            const groupHasSelectedColor = group?.values.includes(selectedColor);
            const selectedValue = groupHasSelectedColor ? selectedColor : null;

            const selectedClass =
                (groupHasSelectedColor && selectedTab === null) ||
                (selectedTab !== null && index === selectedTab)
                  ? 'block'
                  : 'hidden';

            return selectedValue && !selectedlifecycleSubgroup && (
              <div key={index} className="flex items-center gap-2">
                <h3 className="text-label text-darkGray">Color</h3>
                <p key={index} className={`text-sm text-mediumGray ${selectedClass}`}>
                  {selectedValue.split('|').length > 1 ? selectedValue.split('|')[1].trim() : selectedValue}
                </p>
              </div>

            );
          })}
          <div className="w-full">
            {validGroups.map((group, index) => {
              const groupHasSelectedColor = group?.values.includes(selectedColor);

              const selectedClass =
                (groupHasSelectedColor && selectedTab === null) ||
                (selectedTab !== null && index === selectedTab)
                  ? 'block'
                  : 'hidden';

              return (
                <div
                  key={`color-tabs-${index}`}
                  id={`color-tabs-${index}`}
                  role="tabpanel"
                  aria-labelledby={`color-tabs-item-${index}`}
                  className={selectedClass}
                >
                  {renderColorOptions(group)}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          <ProductOptionValuesLabel
            name={name}
            selectedValue={selectedColor}
            isColor={true}
          />
          {renderColorOptions(colorOptions)}
        </>
      )}
    </div>
  ) : (
    <>
     {optionsEnabled && isProductTypePresentOptions ? (
      <div>
        {colorOptions.hasSubGroups ? (
          <div className="flex flex-col gap-2.5">
            {validGroups.map((group, index) => {
              const groupHasSelectedColor = group?.values.includes(selectedColor);
              const selectedValue = groupHasSelectedColor ? selectedColor : null;

              const selectedClass =
                  (groupHasSelectedColor && selectedTab === null) ||
                  (selectedTab !== null && index === selectedTab)
                    ? 'block'
                    : 'hidden';

              return selectedValue && !selectedlifecycleSubgroup && (
                <div key={index} className="flex items-center gap-2">
                  <h3 className="text-label text-darkGray">Color</h3>
                  <p key={index} className={`text-sm text-mediumGray ${selectedClass}`}>
                    {selectedValue.split('|').length > 1 ? selectedValue.split('|')[1].trim() : selectedValue}
                  </p>
                </div>
              );
            })}
            <div className="w-full">
              {validGroups.map((group, index) => {
                const groupHasSelectedColor = group?.values.includes(selectedColor);

                const selectedClass =
                  (groupHasSelectedColor && selectedTab === null) ||
                  (selectedTab !== null && index === selectedTab)
                    ? 'block'
                    : 'hidden';

                return (
                  <div
                    key={`color-tabs-${index}`}
                    id={`color-tabs-${index}`}
                    role="tabpanel"
                    aria-labelledby={`color-tabs-item-${index}`}
                    className={selectedClass}
                  >
                    {renderColorOptions(group)}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            <ProductOptionValuesLabel
              name={name}
              selectedValue={selectedColor}
              isColor={true}
            />
            {renderColorOptions(colorOptions)}
          </>
        )}
      </div>
     ) : (
      <div>
        {colorOptions.hasSubGroups ? (
          <div className="flex flex-col gap-2.5">
            {colorOptions.groups.map((group, index) => {
              if (!group?.values.length) return null;

              const selectedColor = selectedOptions?.[name];
              const groupHasSelectedColor = group?.values.includes(selectedColor);
              const groupName = group.name === 'Default' ? 'Solids' : group.name;
              const colorName = groupName.toLowerCase().trim().endsWith('colors')
                ? groupName
                : `${groupName}`;

              return (
                <div key={index}>
                  <ProductOptionValuesLabel
                    name={colorName}
                    selectedValue={groupHasSelectedColor ? selectedColor : null}
                    isColor={true}
                  />
                  {renderColorOptions(group, selectedColor)}
                </div>
              );
            })}
          </div>
        ) : (
          <>
            <ProductOptionValuesLabel
              name={name}
              selectedValue={selectedOptions?.[name]}
              isColor={true}
            />
            {renderColorOptions(colorOptions, selectedOptions?.[name])}
          </>
        )}
      </div>
     )}
    </>
  );
}

ProductColorOptionValues.displayName = 'ProductColorOptionValues';